import { useEffect, useState } from "react";
import { Crown } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { burst } from "../../lib/fx";
import { getDeviceId } from "../../lib/storage";
import { formatPot, type LmsComp } from "../../lib/lms";
import { LMS_HERO } from "../../data/plTeams";
import { PopButton } from "../PopButton";

export function JoinPanel({ comp, onJoined }: { comp: LmsComp; onJoined: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const closed = comp.history.length > 0 || comp.window === "closed" || comp.status !== "active";
  const full = comp.players.length >= comp.max_players;
  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/lms/${comp.id}/join`, { name, device_id: getDeviceId() });
      toast.success(`You're in, ${name.trim()}! Good luck.`);
      onJoined();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (closed || full) {
    return <section className="rounded-[22px] border-[3px] border-[#0B1F33] bg-[#FFE14D] p-4 font-bold shadow-[0_4px_0_#0B1F33]" data-testid="lms-join-closed">{full ? "This competition is full." : "Entries are closed — the competition has already kicked off."} You can still follow the standings below.</section>;
  }
  return (
    <section className="rounded-[22px] border-[3px] border-[#0B1F33] bg-[#FFE14D] p-4 shadow-[0_4px_0_#0B1F33]" data-testid="lms-join-panel">
      <h2 className="ua-title text-2xl text-[#0B1F33] sm:text-3xl">You're invited!</h2>
      <p className="text-sm font-bold">Enter your name to join <strong>{comp.name}</strong> — {comp.players.length}/{comp.max_players} players so far.</p>
      <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) join(); }}>
        <input className="field" maxLength={30} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="lms-join-name-input" />
        <PopButton type="submit" tone="green" disabled={busy || !name.trim()} data-testid="lms-join-button">Join</PopButton>
      </form>
    </section>
  );
}

export function WinnerScreen({ comp }: { comp: LmsComp }) {
  const winner = comp.players.find((p) => p.id === comp.winner_id);
  useEffect(() => { burst(); }, []);
  return (
    <section className="lms-pitch relative overflow-hidden rounded-[22px] border-[3px] border-[#0B1F33] p-4 text-center text-white shadow-[0_5px_0_#0B1F33] sm:p-6" data-testid="lms-winner-screen">
      <img src={LMS_HERO} alt="Cartoon football standing on a trophy as the last one left" className="mx-auto h-40 w-40 rounded-[20px] border-[3px] border-[#0B1F33] object-cover sm:h-52 sm:w-52" />
      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.25em] text-[#FFE14D]"><Crown className="h-4 w-4" strokeWidth={3} /> Last man standing</p>
      <h2 className="ua-logo mt-1 text-5xl sm:text-7xl" data-testid="lms-winner-name">{winner?.name}</h2>
      <p className="mt-2 font-comic text-xl sm:text-2xl">wins <span className="text-[#FFE14D]" data-testid="lms-winner-pot">{formatPot(comp.prize_pot)}</span> after {comp.history.length} gameweek{comp.history.length === 1 ? "" : "s"}!</p>
      {comp.me?.id === comp.winner_id && <p className="mt-2 inline-block rounded-full bg-[#35E07A] px-3 py-1 text-sm font-extrabold uppercase text-[#0B1F33]">That's you — legend!</p>}
    </section>
  );
}

export function MyStatus({ comp }: { comp: LmsComp }) {
  const me = comp.me!;
  const p = comp.players.find((x) => x.id === me.id);
  const msg = !me.alive
    ? `You're out — GW${p?.eliminated_gw} (${p?.reason === "no_pick" ? "no pick" : p?.reason === "drew" ? "your team drew" : "your team lost"}). Stick around for the drama.`
    : comp.window === "pending" ? `You're still in! Waiting for the admin to open GW ${comp.gameweek} picks.`
    : comp.window === "open" && !me.pick ? "Picks are open — choose your team before the window closes or you're out."
    : comp.window === "closed" ? (me.pick ? `Picks locked. You're on ${me.pick} — fingers crossed!` : "Picks locked and you didn't pick — you'll be eliminated.") : "";
  if (!msg) return null;
  return <section className={`rounded-[18px] border-[3px] border-[#0B1F33] px-4 py-3 font-bold shadow-[0_4px_0_#0B1F33] ${me.alive ? "bg-[#E3F7EA]" : "bg-[#FFE0E0]"}`} data-testid="lms-my-status">{msg}</section>;
}
